/**
 * Players page — the pure "view" layer over the merged roster: filtering, sorting
 * and the data-driven filter options.
 *
 * Kept out of the component (and out of the shared, claim-gated `utils/`) so the
 * behaviour an owner actually depends on — "registered this month, ordered by amount
 * wagered, test accounts hidden" — is unit-testable without rendering. Everything
 * here is a pure function of `(players, view, now)`.
 *
 * WHY these filters: the two data sets together describe an acquisition funnel
 * (registered → logged in → deposited → wagered) plus where each player came from.
 * On a live brand the vast majority of the roster sits at the top of that funnel
 * (verified live: 217 registrations, 5 with any activity, 148 that never returned
 * after signing up), so a flat "all players" list buries every question an owner
 * has. Each filter below isolates one stage or one source of that funnel.
 */
import type { Player } from '../../mock-data/types';
import { isAffiliateCampaignNo, isOwnerTracked } from '../creatives/affiliate-campaign';

/* ---------- sorting ---------- */

export type SortKey =
  | 'registered-desc'
  | 'registered-asc'
  | 'lastseen-desc'
  | 'wagered-desc'
  | 'ggr-desc'
  | 'deposits-desc'
  | 'wins-desc'
  | 'withdrawals-desc'
  | 'email-asc'
  | 'email-desc';

/**
 * Newest registration first. The roster is now the FULL registration list, where
 * almost every row has zero activity, so a money sort would leave ~98% of it in
 * arbitrary order; "who just signed up" is the question that list answers.
 */
export const DEFAULT_SORT: SortKey = 'registered-desc';

/** The stable sort keys (logical ids stored in the URL); labels are translated at
 *  the render site. Order here is the order of the picker. */
export const SORT_KEYS: readonly SortKey[] = [
  'registered-desc',
  'registered-asc',
  'lastseen-desc',
  'wagered-desc',
  'ggr-desc',
  'deposits-desc',
  'wins-desc',
  'withdrawals-desc',
  'email-asc',
  'email-desc',
];

export const isSortKey = (value: string | null): value is SortKey => SORT_KEYS.some((key) => key === value);

/* ---------- filtering ---------- */

/** Funnel stage. `betting` implies wagering; `depositors` implies money in. */
export type ActivityFilter = 'all' | 'betting' | 'depositors' | 'dormant';
/** Registration cohort — how recently the player signed up. */
export type RegisteredFilter = 'all' | '7d' | '30d' | '90d';
/** Engagement recency — how recently the player last logged in. */
export type SeenFilter = 'all' | '7d' | '30d' | 'lapsed' | 'never';

/** `source` sentinel for registrations with no attribution (see {@link sourceOf}). */
export const UNATTRIBUTED = 'unattributed';
/** A source value naming one affiliate: this prefix, then the affiliate's number (empty when signup stored none). */
const AFFILIATE_PREFIX = 'affiliate:';

/** The source value of one affiliate's players. */
export const affiliateSource = (affiliateNo: string): string => `${AFFILIATE_PREFIX}${affiliateNo}`;

/** The affiliate number a source value names ('' for an affiliate signup with no number), or undefined when it names
 *  a channel rather than an affiliate. */
export const affiliateOfSource = (source: string): string | undefined =>
  source.startsWith(AFFILIATE_PREFIX) ? source.slice(AFFILIATE_PREFIX.length) : undefined;

/** The source of a signup through one of the owner's own Studio tracking links (see `isOwnerTracked`). */
export const OWNER_LINK = 'owner_link';

/**
 * Where a player came from, as the Source filter narrows on it: the affiliate who brought them for a signup through an
 * affiliate's link (an affiliates-platform campaign number), {@link OWNER_LINK} for one through the owner's own Studio
 * link (the owner's token as its affiliate part), else the `aft` signup stored. Undefined: not attributed. Signup
 * stores both kinds of link with no `aft`, which left all of them under "Not attributed".
 *
 * Read off the row alone (no tracking links, no affiliates list), so the Source options never change order after they
 * first show; only an affiliate's name fills in once the affiliates list is read.
 */
export const sourceOf = (player: Player): string | undefined => {
  if (isAffiliateCampaignNo(player.campaign)) return affiliateSource(player.affiliate ?? '');
  if (isOwnerTracked(player.affiliate ?? '')) return OWNER_LINK;
  return player.source;
};

/** The channel a source belongs to, for the card and the sheet: `affiliate` for any one affiliate's players (the
 *  "Came from" field names the affiliate), else the source itself. */
export const channelOf = (source: string): string => (affiliateOfSource(source) !== undefined ? 'affiliate' : source);
/** Every filter's "no constraint" value. */
export const ANY = 'all';

export type PlayerFilters = {
  activity: ActivityFilter;
  registered: RegisteredFilter;
  seen: SeenFilter;
  /** `ANY`, `UNATTRIBUTED`, or a source present in this brand's roster (see {@link sourceOf}). */
  source: string;
};

export const DEFAULT_FILTERS: PlayerFilters = {
  activity: ANY,
  registered: ANY,
  seen: ANY,
  source: ANY,
};

const DAY_MS = 24 * 60 * 60 * 1000;

const ACTIVITY_VALUES: readonly ActivityFilter[] = ['all', 'betting', 'depositors', 'dormant'];
const REGISTERED_VALUES: readonly RegisteredFilter[] = ['all', '7d', '30d', '90d'];
const SEEN_VALUES: readonly SeenFilter[] = ['all', '7d', '30d', 'lapsed', 'never'];

/** Read a URL param back into a filter value, falling back to the default. */
const oneOf = <T extends string>(values: readonly T[], raw: string | null, fallback: T): T =>
  values.some((value) => value === raw) ? (raw as T) : fallback;

export const toActivityFilter = (raw: string | null): ActivityFilter => oneOf(ACTIVITY_VALUES, raw, ANY);
export const toRegisteredFilter = (raw: string | null): RegisteredFilter => oneOf(REGISTERED_VALUES, raw, ANY);
export const toSeenFilter = (raw: string | null): SeenFilter => oneOf(SEEN_VALUES, raw, ANY);

/** How many filters are narrowing the list (drives the Filters button's badge). */
export const activeFilterCount = (filters: PlayerFilters): number =>
  (filters.activity === ANY ? 0 : 1) +
  (filters.registered === ANY ? 0 : 1) +
  (filters.seen === ANY ? 0 : 1) +
  (filters.source === ANY ? 0 : 1);

/** Registration-cohort windows, in days back from `now`. */
const REGISTERED_DAYS: Record<Exclude<RegisteredFilter, 'all'>, number> = { '7d': 7, '30d': 30, '90d': 90 };

const matchesActivity = (player: Player, filter: ActivityFilter): boolean => {
  switch (filter) {
    case 'betting':
      return player.bets > 0;
    case 'depositors':
      return player.depositsCount > 0 || player.deposits > 0;
    case 'dormant':
      // Registered but never transacted — the top of the funnel, and the group an
      // owner can still convert.
      return !player.hasActivity;
    default:
      return true;
  }
};

const matchesRegistered = (player: Player, filter: RegisteredFilter, now: number): boolean => {
  if (filter === ANY) return true;
  // A player with no registration row (activity-only) cannot satisfy a cohort window.
  if (player.registeredAt === undefined) return false;
  return player.registeredAt >= now - REGISTERED_DAYS[filter] * DAY_MS;
};

const matchesSeen = (player: Player, filter: SeenFilter, now: number): boolean => {
  if (filter === ANY) return true;
  // "Never" is only an answer for a player whose registration row we actually have:
  // an activity-only row (the registration list hit its cap) has no login field at
  // all, and listing a demonstrable transactor as "never signed in" is a claim the
  // data does not support. `registeredAt` is that row's presence marker, exactly as
  // the detail sheet uses it.
  if (filter === 'never') return player.lastLoginAt === undefined && player.registeredAt !== undefined;
  if (player.lastLoginAt === undefined) return false;
  const age = now - player.lastLoginAt;
  if (filter === 'lapsed') return age > 30 * DAY_MS;
  return age <= (filter === '7d' ? 7 : 30) * DAY_MS;
};

const matchesSource = (player: Player, filter: string): boolean => {
  if (filter === ANY) return true;
  const source = sourceOf(player);
  if (filter === UNATTRIBUTED) return source === undefined;
  return source === filter;
};

/** Apply every active filter. `now` is injected so the result is deterministic. */
export const filterPlayers = (players: readonly Player[], filters: PlayerFilters, now: number): Player[] =>
  players.filter(
    (player) =>
      matchesActivity(player, filters.activity) &&
      matchesRegistered(player, filters.registered, now) &&
      matchesSeen(player, filters.seen, now) &&
      matchesSource(player, filters.source),
  );

/* ---------- filtering by attribution ---------- */

/**
 * Keep the players whose stored `campaign` is in EVERY given set (the Players page's "came from" filter: one link's
 * campaign, and/or every campaign landing on a page; see `attribution.ts`). No sets: no constraint. A player with no
 * campaign never matches a set, so an activity-only row (no registration, hence no campaign) drops out. Pure.
 */
export const filterByCampaignSets = (
  players: readonly Player[],
  sets: readonly ReadonlySet<string>[],
): Player[] => {
  if (sets.length === 0) return [...players];
  return players.filter((player) => {
    const campaign = player.campaign?.trim() ?? '';
    return campaign !== '' && sets.every((set) => set.has(campaign));
  });
};

/* ---------- sorting ---------- */

// Rows missing the sort field (no registration row, never logged in) always sink to
// the bottom — in BOTH directions, so "oldest first" doesn't open on a block of
// unknowns. -Infinity does that for a descending compare; ascending negates it.
const MISSING = -Infinity;

/** The numeric key a sort compares on, descending. */
const sortValue = (player: Player, key: SortKey): number => {
  switch (key) {
    case 'registered-desc':
    case 'registered-asc':
      return player.registeredAt ?? MISSING;
    case 'lastseen-desc':
      return player.lastLoginAt ?? MISSING;
    case 'wagered-desc':
      return player.wagered;
    case 'ggr-desc':
      return player.ggr;
    case 'deposits-desc':
      return player.deposits;
    case 'wins-desc':
      return player.wins;
    case 'withdrawals-desc':
      return player.withdrawals;
    default:
      return 0;
  }
};

/** Email order, `direction` 1 or -1. An account with no email sinks in both directions, like any other missing value;
 *  two of them fall back to their ids. */
const byEmail = (a: Player, b: Player, direction: number): number => {
  if ((a.email === '') !== (b.email === '')) return a.email === '' ? 1 : -1;
  return direction * a.email.localeCompare(b.email) || a.id.localeCompare(b.id);
};

/**
 * Sort a copy of the roster. Ties break on email (then id) so paging is deterministic —
 * with most of the roster sharing a value of `0`, an unstable order would otherwise let
 * rows hop between pages on re-render.
 */
export const sortPlayers = (players: readonly Player[], key: SortKey): Player[] => {
  const sorted = [...players];
  if (key === 'email-asc' || key === 'email-desc') {
    const direction = key === 'email-asc' ? 1 : -1;
    return sorted.sort((a, b) => byEmail(a, b, direction));
  }
  const ascending = key === 'registered-asc';
  return sorted.sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av !== bv) {
      // Missing values sink in both directions, so they never lead an ascending sort.
      if (av === MISSING) return 1;
      if (bv === MISSING) return -1;
      return ascending ? av - bv : bv - av;
    }
    return byEmail(a, b, 1);
  });
};

/* ---------- data-driven source options ---------- */

export type SourceOption = { value: string; count: number };

/**
 * The acquisition sources actually present in this brand's roster (each affiliate on its own), most common first,
 * with an `unattributed` bucket when some registrations carry no attribution (see {@link sourceOf}).
 *
 * Derived from the data rather than hard-coded: `aft` is an open set the backend
 * grows (`direct`, `organic`, `affiliate`, `web`, `app`, `crypto`, `video`,
 * `influencers`, …), and offering a brand a source it has zero players from is a
 * dead control.
 */
export const sourceOptions = (players: readonly Player[]): SourceOption[] => {
  const counts = new Map<string, number>();
  players.forEach((player) => {
    const key = sourceOf(player) ?? UNATTRIBUTED;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  // Channels first, then one entry per affiliate, each by most players; the unattributed bucket always last (it is not
  // a channel).
  const rank = (value: string): number => (value === UNATTRIBUTED ? 2 : affiliateOfSource(value) !== undefined ? 1 : 0);
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => rank(a.value) - rank(b.value) || b.count - a.count || a.value.localeCompare(b.value));
};

// NOTE: the `players` data set also carries a `test_account` flag, and it is
// deliberately NOT surfaced. A brand owner has no test accounts — every row in
// their roster is a real player to them — so a "hide test accounts" control would
// only invite them to hide their own customers. The field stays documented in
// `docs/api.owners-hub.stats.md` for whoever needs it next.

// Acquisition-source DISPLAY labels deliberately do NOT live here: the i18n
// extractor only harvests literal `t('…')` / `<Text text="…">` call sites, so a
// module-scope label map would never reach the catalogs. See `useSourceLabel.ts`.
